import { OperationVariables } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import type { Point } from 'geojson';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { JobPositionForCandidateDto } from '@pflegia/types/api/job-position/job-position';
import { PaginationSortField } from '@pflegia/types/common/job-position/pagination-sort-field';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { isDefined } from '@pflegia/util/functions/type-guards';

import { GET_JOB_POSITIONS_BY_EMPLOYER } from '@shared/graphql/operations/job-positions-by-employer';
import { GET_POSITION_RELATED_JOB_LIST } from '@shared/graphql/operations/position-related-job-list';

export const JOBS_FOR_EMPLOYER_PAGE_SIZE = 2;
export const RELATED_POSITION_JOBS_PAGE_SIZE = 3;
type JobPositionDataType = { jobPositions: JobPositionForCandidateDto[]; total: number };

export interface PositionRelatedJobsFilters {
  employerId: string;
  excludedJobPositionIds: string[];
  location: Point;
  jobTitleIds?: string[];
  jobPositionTitleQueries: string[];
}

interface GetJobPositionsByEmployerProps {
  employerId?: string;
  jobPositionId?: string;
  populateEmployerRelatedJobs?: boolean;
  positionRelatedJobsFilters?: PositionRelatedJobsFilters;
}

export const useGetOtherJobsForEmployer = ({
  employerId,
  jobPositionId,
  populateEmployerRelatedJobs = false,
  positionRelatedJobsFilters,
}: GetJobPositionsByEmployerProps) => {
  const authenticated = useAuth();
  const [page, setPage] = useState(0);
  const [jobs, setJobs] = useState<JobPositionForCandidateDto[]>([]);
  const [total, setTotal] = useState<number>(0);

  const updateJobs = useCallback(
    (jobPositionsData: JobPositionDataType) => {
      setTotal(jobPositionsData.total);
      if (page === 0) {
        setJobs(jobPositionsData.jobPositions);
        return;
      }
      setJobs(currentJobs => {
        const mergedJobs = Array.from(currentJobs);
        jobPositionsData.jobPositions.forEach(resultJob => {
          const existingJobIndex = currentJobs.findIndex(dto => dto.id === resultJob.id);
          if (existingJobIndex === -1) {
            mergedJobs.push(resultJob);
            return;
          }
          if (!currentJobs[existingJobIndex].match && resultJob.match) {
            mergedJobs[existingJobIndex] = resultJob;
          }
        });
        return mergedJobs;
      });
    },
    [page],
  );

  const variables = useMemo(
    () => ({
      employerId,
      jobPositionId,
      page,
      perPage: JOBS_FOR_EMPLOYER_PAGE_SIZE,
    }),
    [employerId, jobPositionId, page],
  );
  const {
    data: jobPositionsByEmployerData,
    loading,
    refetch: baseRefetch,
  } = useQuery<{ jobPositionsData: JobPositionDataType }>(GET_JOB_POSITIONS_BY_EMPLOYER, {
    variables,
    skip: !employerId || !populateEmployerRelatedJobs,
  });
  useEffect(() => {
    if (!jobPositionsByEmployerData) return;
    updateJobs(jobPositionsByEmployerData.jobPositionsData);
  }, [jobPositionsByEmployerData, updateJobs]);

  const { data: relatedJobListData, loading: loadingPositionRelatedJobList } = useQuery<{ jobPositionsData: JobPositionDataType }>(
    GET_POSITION_RELATED_JOB_LIST,
    {
      fetchPolicy: authenticated ? 'network-only' : 'cache-first',
      variables: {
        page,
        perPage: RELATED_POSITION_JOBS_PAGE_SIZE,
        filters: positionRelatedJobsFilters,
        sortField: PaginationSortField.Salary,
      },
      skip: populateEmployerRelatedJobs || !isDefined(positionRelatedJobsFilters),
    },
  );
  useEffect(() => {
    if (!relatedJobListData) return;
    updateJobs(relatedJobListData.jobPositionsData);
  }, [relatedJobListData, updateJobs]);

  const refetch = useCallback(
    async (extraVariables?: OperationVariables) => {
      if (!variables.employerId && !extraVariables?.employerId) return;
      const { data } = await baseRefetch({ ...variables, ...extraVariables });
      if (data) updateJobs(data.jobPositionsData);
    },
    [baseRefetch, updateJobs, variables],
  );

  useEffect(() => {
    if (jobPositionId) {
      setPage(0);
    }
  }, [jobPositionId]);

  return { jobs, total, loading: loading || loadingPositionRelatedJobList, setPage, refetch };
};
